'use client'

import { MessageCircle, X } from 'lucide-react'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'

import { RADIUS } from '@/lib/surface'
import { cn } from '@/lib/utils'

/**
 * The FLOU assistant, as a dock at the foot of every public page.
 *
 * Owner's call: *„czat ten jest w formie wyskakującego okienka do dole strony,
 * nie na stronie głównej"*. It used to be a cell in the home page's bento —
 * one column, two rows, desktop only — which put the one thing on the site
 * that can answer a question in exactly one place, and only for people who
 * scrolled no further than the first screen. Somebody reading the montage
 * price list had no way to ask about it.
 *
 * ── NOTHING LOADS UNTIL SOMEBODY ASKS ────────────────────────────────────
 * The tile had to be gated behind a media query, because a client island
 * inside a hidden box still hydrates and still pulls the whole AI-SDK chat
 * runtime — a chunk fetched, parsed and executed on a throttled phone for
 * pixels nobody could see.
 *
 * A dock does not need that gate, and gets something better: the chunk is
 * imported when the panel OPENS. Until then this component is a button. That
 * is why the phone can have it now — the cost it was being spared is no longer
 * paid by anyone who does not want the conversation.
 *
 * ── THE PANEL IS NOT A MODAL ─────────────────────────────────────────────
 * No overlay, no focus trap, no scroll lock. It sits over the corner of a page
 * somebody is still reading, which is the point: the price list stays visible
 * while they ask about it. `Escape` closes it and the launcher takes focus
 * back, because a thing that opens on click has to close on the key everybody
 * tries first.
 */

const BentoAssistant = dynamic(
  () => import('@/components/sections/bento-assistant.client').then((m) => m.BentoAssistant),
  { ssr: false },
)

export function AssistantDock({ locale }: { locale: string }) {
  const [open, setOpen] = useState(false)
  /** Mounted once opened and kept, so closing the panel does not lose the thread. */
  const [everOpened, setEverOpened] = useState(false)
  const launcherRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      launcherRef.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col items-end gap-3 p-4 md:p-6">
      {/* HIDDEN, not unmounted. A conversation somebody closed to re-read a
          price is the same conversation when they open it again — unmounting
          would greet them from the top as a stranger. `hidden` keeps the
          runtime alive without keeping it on screen or in the tab order. */}
      {everOpened ? (
        <section
          aria-label="Czat z FLOU"
          hidden={!open}
          className={cn(
            RADIUS.box,
            'pointer-events-auto flex h-[min(32rem,calc(100dvh-9rem))] w-[min(23rem,calc(100vw-2rem))] flex-col overflow-hidden border border-white/[0.14] bg-[var(--flou-surface)]/95 shadow-[0_40px_120px_-40px_rgb(0_0_0/0.9)] backdrop-blur-xl',
          )}
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
            <p className="text-[0.9rem] font-semibold text-white">Zapytaj FLOU</p>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                launcherRef.current?.focus()
              }}
              aria-label="Zamknij czat"
              className="inline-flex size-8 items-center justify-center rounded-full border border-white/12 text-white/60 transition hover:border-white/30 hover:text-white"
            >
              <X size={15} aria-hidden="true" />
            </button>
          </header>

          <div className="flex min-h-0 flex-1 flex-col p-4">
            <BentoAssistant locale={locale} />
          </div>
        </section>
      ) : null}

      <button
        ref={launcherRef}
        type="button"
        aria-expanded={open}
        onClick={() => {
          setEverOpened(true)
          setOpen((was) => !was)
        }}
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-[0.9rem] font-semibold text-[#16101c] shadow-[0_18px_40px_-18px_rgb(0_0_0/0.9)] transition-transform duration-300 hover:scale-[1.04]"
      >
        {open ? <X size={17} aria-hidden="true" /> : <MessageCircle size={17} aria-hidden="true" />}
        {/* The label is the whole offer. „Czat" names the widget; this names
            what it is for, and on a phone the icon carries it alone. */}
        <span className="hidden sm:inline">{open ? 'Zamknij' : 'Zapytaj FLOU'}</span>
      </button>
    </div>
  )
}
